/**
 * EditorJsonPanel — модал «JSON» в редакторе квизов (Фаза 12, D3).
 *
 * Две вкладки:
 *  - «Мой квиз» — живой read-only предпросмотр ТЕКУЩЕГО черновика в формате
 *    экспорта (serializeDraft из drafts.ts — тот же JSON, что у «Экспорт JSON»).
 *    Кнопка «Копировать» — async Clipboard API с fallback на execCommand
 *    (старые Safari/HTTP-контексты), состояние «Скопировано» на 1.5 с.
 *  - «Справочник» — статические образцы ВСЕХ шести типов вопросов
 *    (choice/poll/open/match/order/word) с локализованным описанием полей:
 *    пользователь видит, как задавать вопросы через JSON (и вставлять их
 *    модалом «Вставить из JSON», D4). Значения образцов локале-нейтральны,
 *    чтобы справочник читался одинаково в ru/en/cn; у каждого образца своя
 *    кнопка «Копировать» (скопировал → вставил в D4).
 *
 * Модал рендерится через createPortal в document.body: position:fixed внутри
 * .card (backdrop-filter) в Chromium позиционировался бы относительно карточки,
 * а не viewport. Доступность: role="dialog" + aria-modal + aria-label, Escape
 * и клик по оверлею закрывают, фокус — на контейнер диалога при открытии;
 * вкладки — role="tablist"/"tab" c aria-selected + tabpanel.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { serializeDraft, type QuizDraft } from '../../drafts';
import { KINDS, type Kind } from '../../protocol';

export interface EditorJsonPanelProps {
  /** Текущий черновик — живой источник предпросмотра вкладки «Мой квиз». */
  draft: QuizDraft;
  onClose: () => void;
}

/**
 * Статические образцы всех типов (структура 1:1 DraftQuestion из drafts.ts;
 * локале-нейтральные значения; multi — Фаза 14, эпик C, плоское тело §2.6 спеки).
 * Валидный JSON — без //-комментариев, чтобы образец можно было скопировать и
 * вставить в «Вставить из JSON» (D4) как есть.
 */
const REFERENCE_SAMPLES: Record<Kind, string> = {
  choice: `{
  "title": "Capital of France?",
  "durationMs": 20000,
  "body": {
    "kind": "choice",
    "options": ["Paris", "London", "Berlin"],
    "correctIndex": 0
  }
}`,
  poll: `{
  "title": "Which feature matters most?",
  "durationMs": 30000,
  "body": {
    "kind": "poll",
    "options": ["Speed", "Simplicity", "Price"]
  }
}`,
  open: `{
  "title": "Name a gas giant planet",
  "durationMs": 20000,
  "body": {
    "kind": "open",
    "accept": ["Jupiter", "Saturn"],
    "maxLen": 120
  }
}`,
  match: `{
  "title": "Match countries with capitals",
  "durationMs": 40000,
  "body": {
    "kind": "match",
    "pairs": [
      { "left": "France", "right": "Paris" },
      { "left": "Japan", "right": "Tokyo" }
    ]
  }
}`,
  order: `{
  "title": "Arrange planets from the Sun",
  "durationMs": 40000,
  "body": {
    "kind": "order",
    "items": ["Earth", "Mars", "Mercury"],
    "correctOrder": [2, 1, 0]
  }
}`,
  word: `{
  "title": "Describe your mood in one word",
  "durationMs": 25000,
  "body": {
    "kind": "word",
    "maxLen": 40
  }
}`,
  // Канонический образец multi из §2.6 спеки phase-14-interface-spec (плоское
  // тело: options + correct-индексы с 0; лишняя отметка — 0 очков).
  multi: `{
  "title": "Select all gas giants",
  "durationMs": 30000,
  "body": {
    "kind": "multi",
    "options": ["Jupiter", "Mars", "Saturn", "Neptune"],
    "correct": [0, 2, 3]
  }
}`,
  // Канонический образец run из §3.5 спеки phase-14-interface-spec (плоское
  // тело: 4 угла + correctIndex; seed выдаёт сервер на каждый старт).
  run: `{
  "title": "Run to the corner: capitals",
  "durationMs": 45000,
  "body": {
    "kind": "run",
    "corners": ["Paris", "London", "Madrid", "Berlin"],
    "correctIndex": 0
  }
}`,
};

/**
 * 9-й образец «вопрос с блоком кода» (Фаза 16, B4) — ВНЕ Record<Kind, string>
 * (решение плана §7): блок кода ортогонален типу вопроса (code/codeLang —
 * верхний уровень DraftQuestion), поэтому живёт отдельной секцией справочника,
 * а не «типовым» списком. Значения локале-нейтральны; переносы/табы внутри
 * code — JSON-escape (\n, \t), образец копируется и вставляется в «Вставить
 * из JSON» (D4) как есть. codeLang — из whitelist (game.CodeLangs/languages.ts).
 */
const CODE_BLOCK_SAMPLE = `{
  "title": "What does this Go snippet print?",
  "durationMs": 30000,
  "code": "package main\\n\\nimport \\"fmt\\"\\n\\nfunc main() {\\n\\tfmt.Println(40 + 2)\\n}",
  "codeLang": "go",
  "body": {
    "kind": "choice",
    "options": ["40", "42", "44"],
    "correctIndex": 1
  }
}`;

/** Копирует текст в буфер: async Clipboard API, fallback — execCommand. */
async function copyText(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* clipboard занят/недоступен (нет фокуса, HTTP) — пробуем fallback ниже */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

/** Сколько секунд-миллисекунд показываем «Скопировано» после успешного копирования. */
const COPIED_RESET_MS = 1500;

export function EditorJsonPanel({ draft, onClose }: EditorJsonPanelProps): JSX.Element {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'quiz' | 'ref'>('quiz');
  const [copied, setCopied] = useState(false);
  /** kind последнего успешно скопированного образца (вкладка «Справочник»);
   * 'code' — 9-й образец блока кода (вне Record<Kind, string>). */
  const [sampleCopied, setSampleCopied] = useState<Kind | 'code' | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Фокус на диалог при открытии (keyboard-пользователь сразу «внутри»), Escape — закрыть.
  useEffect(() => {
    dialogRef.current?.focus();
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleCopyQuiz(): Promise<void> {
    const ok = await copyText(serializeDraft(draft));
    setCopied(ok);
    if (ok) {
      window.setTimeout(() => setCopied(false), COPIED_RESET_MS);
    }
  }

  async function handleCopySample(kind: Kind | 'code'): Promise<void> {
    const text = kind === 'code' ? CODE_BLOCK_SAMPLE : REFERENCE_SAMPLES[kind];
    const ok = await copyText(text);
    setSampleCopied(ok ? kind : null);
    if (ok) {
      window.setTimeout(() => setSampleCopied(null), COPIED_RESET_MS);
    }
  }

  return createPortal(
    <div
      className="modal-overlay"
      onClick={onClose}
      data-testid="editor-json-overlay"
      role="presentation"
    >
      <div
        ref={dialogRef}
        className="modal json-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t('host.editor.json.title')}
        tabIndex={-1}
        data-testid="editor-json-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal__head">
          <h3 className="modal__title">{t('host.editor.json.title')}</h3>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onClose}
            data-testid="editor-json-close"
          >
            {t('host.editor.json.close')}
          </button>
        </div>

        <div className="json-tabs" role="tablist" aria-label={t('host.editor.json.title')}>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'quiz'}
            className="json-tab"
            data-testid="editor-json-tab-quiz"
            onClick={() => setTab('quiz')}
          >
            {t('host.editor.json.tabQuiz')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'ref'}
            className="json-tab"
            data-testid="editor-json-tab-ref"
            onClick={() => setTab('ref')}
          >
            {t('host.editor.json.tabRef')}
          </button>
        </div>

        {tab === 'quiz' ? (
          <div
            className="modal__body"
            role="tabpanel"
            aria-label={t('host.editor.json.tabQuiz')}
            data-testid="editor-json-panel-quiz"
          >
            <div className="json-panel__bar">
              <span className="muted small">{t('host.editor.json.quizHint')}</span>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => void handleCopyQuiz()}
                data-testid="editor-json-copy"
              >
                {copied ? t('host.editor.json.copied') : t('host.editor.json.copy')}
              </button>
            </div>
            <pre className="json-code" data-testid="editor-json-preview">
              <code>{serializeDraft(draft)}</code>
            </pre>
          </div>
        ) : (
          <div
            className="modal__body"
            role="tabpanel"
            aria-label={t('host.editor.json.tabRef')}
            data-testid="editor-json-panel-ref"
          >
            <p className="muted small" data-testid="editor-json-ref-intro">
              {t('host.editor.json.refIntro')}
            </p>
            {KINDS.map((kind) => (
              <div key={kind} className="json-sample" data-testid={`editor-json-sample-${kind}`}>
                <div className="json-sample__head">
                  <span className="json-sample__kind">
                    {t(`kind.${kind}`)} <code>{kind}</code>
                  </span>
                  <button
                    type="button"
                    className="btn btn--ghost json-sample__copy"
                    onClick={() => void handleCopySample(kind)}
                    data-testid={`editor-json-sample-copy-${kind}`}
                  >
                    {sampleCopied === kind
                      ? t('host.editor.json.copied')
                      : t('host.editor.json.copy')}
                  </button>
                </div>
                <p className="muted small json-sample__note">{t(`host.editor.json.ref.${kind}`)}</p>
                <pre className="json-code">
                  <code>{REFERENCE_SAMPLES[kind]}</code>
                </pre>
              </div>
            ))}

            {/* 9-й образец (Фаза 16, B4): блок кода ортогонален типу вопроса —
                отдельная секция вне Record<Kind, string> (решение плана §7). */}
            <div className="json-sample" data-testid="editor-json-sample-code">
              <div className="json-sample__head">
                <span className="json-sample__kind">{t('host.editor.code.sampleTitle')}</span>
                <button
                  type="button"
                  className="btn btn--ghost json-sample__copy"
                  onClick={() => void handleCopySample('code')}
                  data-testid="editor-json-sample-copy-code"
                >
                  {sampleCopied === 'code'
                    ? t('host.editor.json.copied')
                    : t('host.editor.json.copy')}
                </button>
              </div>
              <p className="muted small json-sample__note">{t('host.editor.code.sampleNote')}</p>
              <pre className="json-code">
                <code>{CODE_BLOCK_SAMPLE}</code>
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
